import React from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";

// ─── Constants ────────────────────────────────────────────────────────────────

export const CLIENT_TYPE_OPTIONS = [
  "", "Not Specified", "Drinking Water", "Drinking Water NonReg", "Waste Water",
  "IndoorPool", "OutdoorPool", "Multiple", "ETK", "ETK Distributor",
  "ETK Wholesale", "BathingBeach", "FarmOut", "PoolManagement",
];

export const BILLING_CODE_OPTIONS = ["", "BOOK", "CONTRACT", "DISCOUNT"];

export const SYSTEM_TYPE_OPTIONS = ["", "None", "Public", "Individual"];

export const FILE_FORMAT_OPTIONS = [
  { value: "PDF", label: "PDF (.pdf)" },
  { value: "EXCELOPENXML", label: "Excel (.xlsx)" },
  { value: "EXCEL", label: "Excel Legacy (.xls)" },
  { value: "CSV", label: "CSV (.csv)" },
  { value: "WORDOPENXML", label: "Word (.docx)" },
  { value: "XML", label: "XML (.xml)" },
];

export const SHIP_BY_OPTIONS = [
  "UPS",
  "Pickup Madison Lab",
  "Pickup Carmel Lab",
  "Pickup Lafayette Lab",
  "FedEx",
  "Routine Route-Black",
  "Routine Route-Black/Gold",
  "Routine Route-Orange",
  "Routine Route-Blue",
  "Routine Route-Red",
  "Routine Route-Purple",
  "Routine Route-Green",
  "Routine Route",
  "ClientWalkIn",
  "PickUp",
  "UPS - with Return Label",
];

export const STEPS = [
  "Basic Info",
  "Reporting",
  "Billing",
  "Shipping",
  "Field",
  "Management",
  "System",
  "Notes & Settings",
];

export const INITIAL_FORM_STATE = {
  accountNumber: "", clientCode: "", pwsNumber: "", clientName: "",
  reportingName: "", reportAddress: "", reportAddress2: "", reportCity: "",
  reportState: "", reportZip: "", reportCountry: "", isDeleted: false,
  reportPhone1: "", reportPhone2: "", reportFax: "", reportEmail: "",
  reportContact: "", reportFormat: "", billingName: "", billingAddress: "",
  billingAddress2: "", billingCity: "", billingState: "", billingZip: "",
  billingCountry: "", billingPhone1: "", billingPhone2: "", billingFax: "",
  billingEmail: "", billingContact: "", billingFormat: "", shippingName: "",
  shippingAddress: "", shipAddress2: "", shippingState: "", shippingCity: "",
  shippingZip: "", shippingCountry: "", shippingPhone1: "", shippingPhone2: "",
  shippingFax: "", shippingEmail: "", shippingContact: "", shippingFormat: "",
  sicCode: "", clientNotes: "", resetPasswordToken: "", billingCode: "",
  discountPercent: "", surchargePercent: "", customerType: "", salesPerson: "",
  labRep: "", poNumber: "", taxable: false, shortTerm: false, status: "",
  expirationDate: "", specialInstructions: "", remotePassword: "",
  reportToLocalAgency: false, reportToFederalAgency: false, dateCreated: "",
  createdBy: "", taDays: "", reportingPhone1Type: "", reportingPhone2Type: "",
  shippingPhone1Type: "", shippingPhone2Type: "", billingPhone1Type: "",
  billingPhone2Type: "", carbonCopy: false, faxReport: false,
  projectManager: "", divisionCode: "", reportingComments: "",
  billingComments: "", shippingComments: "", glCode: "", systemName: "",
  systemType: "", groupType: "", fieldContact: "", fieldPhone: "",
  fieldPhone2: "", fieldFax: "", fieldEmail: "", fieldNotes: "",
  permitNumber: "", fieldAddress: "", fieldAddress2: "", fieldCity: "",
  fieldState: "", fieldZip: "", processId: "", county: "", holdComments: "",
  hold: false, dohsource: "", multiplesource: "", defaultLabReport: "",
  defaultInvoiceReport: "", labReportFileFormat: "", invoiceReportFileFormat: "",
  grantaccesstoallusers: false, lastUpdatedBy: "", timestamp: "", recordid: "",
  contractExp: "", surchargeExempt: false, printBit: false, emailBit: false,
  eomBit: false, clientType: "", syncWithKeap: false, orderTemplate: "", regulatoryEmail: "",
  sendToRegulatory: false, qbAccountNumber: "", billingEmailText: "",
  creditAmount: "", complianceNotes: "", routineShipment: "", shipBy: "",
  routeFrequency: "", routeCollection: "", enableSampleReceipt: false,
  emailRegulatoryOnly: false, eowBit: false, shouldReceiveCoC: false,
  newAccountExempt: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const toArray = (val) =>
  val ? val.split(",").map((v) => v.trim()).filter(Boolean) : [];

export const toString = (arr) => arr.join(",");

// ─── Shared Sub-Components ────────────────────────────────────────────────────

export function MultiFormatSelect({ label, name, value, onChange }) {
  const selected = toArray(value);

  const handleMultiChange = (e) => {
    const { value: newVal } = e.target;
    const asString = toString(
      typeof newVal === "string" ? newVal.split(",") : newVal
    );
    onChange({ target: { name, value: asString } });
  };

  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        name={name}
        value={selected}
        onChange={handleMultiChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selectedVals) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedVals.map((val) => {
              const option = FILE_FORMAT_OPTIONS.find((o) => o.value === val);
              return <Chip key={val} label={option?.label ?? val} size="small" />;
            })}
          </Box>
        )}
      >
        {FILE_FORMAT_OPTIONS.map(({ value: optVal, label: optLabel }) => (
          <MenuItem key={optVal} value={optVal}>
            {optLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function ReportSelect({ label, name, value, onChange, reports }) {
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select name={name} value={value || ""} onChange={onChange} label={label}>
        <MenuItem value=""><em>None</em></MenuItem>
        {reports.map(({ msrsReport, description }) => (
          <MenuItem key={msrsReport} value={msrsReport}>
            {description}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function SimpleSelect({ label, name, value, onChange, options }) {
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select name={name} value={value ? value.trim() : ""} onChange={onChange} label={label}>
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt === "" ? <em>None</em> : opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
